"use client";

import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';

const COLORS = { recycled: '#10b981', savings: '#3b82f6' };

const WASTE_TYPES = [
  { key: 'all',      label: 'All' },
  { key: 'organic',  label: 'Organic' },
  { key: 'pmd',      label: 'PMD' },
  { key: 'paper',    label: 'Paper' },
  { key: 'residual', label: 'Residual' },
];

const CO2SavingsCard = ({
  id = 'R6',
  wasteitem = 'organic',
  dayvalue = 'year',
  datevalue = [{ startDate: null, endDate: null, key: 'selection' }],
  selectedButton_ = 'year'
}) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWasteType, setSelectedWasteType] = useState('all');
  const { t } = useTranslation();

  const translations = {
    chartTitle:    t('co2SavingsChart.chartTitle')    || "CO₂ Savings",
    recycledLabel: t('co2SavingsChart.recycledLabel') || "CO₂ Production",
    savingsLabel:  t('co2SavingsChart.savingsLabel')  || "CO₂ Savings",
    unsortedLabel: t('co2SavingsChart.unsortedLabel') || "Unsorted CO₂ Production",
    loading:       t('co2SavingsChart.loading')       || "Loading data...",
    loadError:     t('co2SavingsChart.loadError')     || "Failed to load data.",
    noData:        t('co2SavingsChart.noData')        || "No data available for selected period"
  };

  const isCustom = selectedButton_ === 'custom';

  const formatForBackend = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    return d.toDateString();
  };

  const customDateRangeKey = (() => {
    if (!isCustom) return null;
    const r = datevalue?.[0];
    if (!r?.startDate || !r?.endDate) return null;
    const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
    const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
    return `${start}__${end}`;
  })();

  // Resolve which waste type(s) to fetch
  const resolvedWasteItem = selectedWasteType === 'all' ? wasteitem : selectedWasteType;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        id,
        wasteitem: resolvedWasteItem,
        day: dayvalue,
        withcalendar: isCustom,
      };

      if (isCustom && datevalue?.[0]?.startDate && datevalue?.[0]?.endDate) {
        params.startDate = formatForBackend(datevalue[0].startDate);
        params.endDate   = formatForBackend(datevalue[0].endDate);
      }

      const response = await axiosInstance.get("/co2savings", { params, timeout: 10000 });

      if (!response.data) throw new Error(translations.loadError);

      let transformedData = [];
      if (response.data.savings && response.data.recycled) {
        transformedData = Object.keys(response.data.savings).map(key => ({
          name:     key,
          recycled: response.data.recycled[key] || 0,
          savings:  response.data.savings[key]  || 0
        }));
      } else if (Array.isArray(response.data)) {
        transformedData = response.data.map(item => ({
          name:     item.date || item.name,
          recycled: item.recycled || 0,
          savings:  item.savings  || 0
        }));
      }

      setChartData(transformedData);
    } catch (err) {
      console.error("API Error:", err);
      if (resolvedWasteItem !== 'pmd') setError(err.message || translations.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && resolvedWasteItem && dayvalue) fetchData();
  }, [id, resolvedWasteItem, dayvalue, selectedButton_, customDateRangeKey]);

  const totals = chartData.reduce(
    (acc, item) => ({ recycled: acc.recycled + item.recycled, savings: acc.savings + item.savings }),
    { recycled: 0, savings: 0 }
  );

  // Waste type button styles — same pill style as sub-waste heatmap
  const getButtonStyle = (key) => {
    const isActive = selectedWasteType === key;
    const colorMap = {
      all:      isActive ? 'bg-gray-700 text-white border-gray-700'      : 'bg-transparent text-gray-600 border-gray-300 hover:border-gray-500',
      organic:  isActive ? 'bg-green-600 text-white border-green-600'    : 'bg-transparent text-green-700 border-green-300 hover:border-green-500',
      pmd:      isActive ? 'bg-blue-500 text-white border-blue-500'      : 'bg-transparent text-blue-600 border-blue-300 hover:border-blue-400',
      paper:    isActive ? 'bg-yellow-500 text-white border-yellow-500'  : 'bg-transparent text-yellow-700 border-yellow-300 hover:border-yellow-400',
      residual: isActive ? 'bg-red-500 text-white border-red-500'        : 'bg-transparent text-red-600 border-red-300 hover:border-red-400',
    };
    return `px-2.5 py-0.5 rounded-full border text-xs font-medium transition-all duration-150 cursor-pointer ${colorMap[key]}`;
  };

  return (
    <div className="sm:rounded-lg bg-muted/50 p-2">
      {/* Header row: title + waste type buttons */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h2 className="scroll-m-20 text-xl font-semibold tracking-tight">{translations.chartTitle}</h2>

        {/* Waste type selector pills */}
        <div className="flex flex-wrap gap-1">
          {WASTE_TYPES.map(({ key, label }) => (
            <button
              key={key}
              className={getButtonStyle(key)}
              onClick={() => setSelectedWasteType(key)}
            >
              {key === 'all' ? (t('common.all') || 'All') : (t(`wasteImpact.streams.${key}`) || label)}
            </button>
          ))}
        </div>
      </div>

      <div className="aspect-[4/3] md:aspect-[16/9]">
        <div className="flex flex-col h-full w-full gap-0">
          <div className="flex-1 min-h-0">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <p>{translations.loading}</p>
              </div>
            ) : error && resolvedWasteItem !== 'pmd' ? (
              <div className="h-full flex items-center justify-center p-4">
                <p className="text-red-500">{error}</p>
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} width={30} />
                  <Area type="natural" dataKey="recycled" stroke={COLORS.recycled} fill={COLORS.recycled} fillOpacity={0.2} strokeWidth={2} />
                  <Area type="natural" dataKey="savings"  stroke={COLORS.savings}  fill={COLORS.savings}  fillOpacity={0.2} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">{translations.noData}</p>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex justify-center mt-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.recycled }} />
                <span className="text-xs font-medium">{translations.recycledLabel}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-0.5" style={{ backgroundColor: COLORS.savings }} />
                <span className="text-xs font-medium">{translations.unsortedLabel}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full opacity-20" style={{ backgroundColor: COLORS.savings }} />
                <span className="text-xs font-medium">{translations.savingsLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CO2SavingsCard;